import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import {
  H2,
  H3,
  H4,
  H5,
  P,
  Disclaimer,
  Ul,
  Li,
  BlueSub,
} from "../components/el/typography"
import { ProdHero, TechStack } from "../components/ui/prodhero"

import {
  Block,
  Header,
  Section,
  Card,
  CardBody,
  CardImage,
  CardWrap,
  CardSub,
  CardList,
  CardLi,
} from "../components/ui/mastercard"
import { ContactCTA, SeriesCTA } from "../components/ui/cta"
import { ProdCTAlink } from "../components/el/button"
import ProductList from "../components/ui/productlist"
import RcProtocolTable from "../components/parts/rcprotocoltable"
import { Seo } from "../components/seo"
const Rc = ({ data }) => {
  const {
    logo,
    hero,
    flaskpn,
    rcpn,
    action,
    workday,
    filtration,
    oldM,
    newM,
    hplcgraph,
    gel,
    protocolgraph,
  } = data

  return (
    <Layout noheader>
      <ProdHero
        alt={hero.name}
        gatsbyImageData={hero.childImageSharp.gatsbyImageData}
      >
        <H2 className="mt-0 text-center sm:mt-0 lg:text-left">
          {logo.siteMetadata.brand.rc} <BlueSub>Cap</BlueSub>
        </H2>
        <TechStack
          color="purple"
          sort="Optimum Growth System > Rapid Clear"
          gi="../faqrc"
          sample="RC"
          techQuery="/?categories=Optimum+Growth+System_Rapid+Clear"
          parts
        ></TechStack>
        <P className="text-center sm:mx-auto sm:max-w-xl md:mt-5 lg:mx-0 lg:text-left">
          A revolutionary new technology that allows high speed clarification of
          cellular material
        </P>
      </ProdHero>

      <Block theme="whitefull">
        <Header center normal>
          <H2>Clarify 3L of Cell Culture In &lt; 30 Minutes</H2>
          <H5>with no centrifugation required</H5>
        </Header>
        <Section onecard snug>
          <CardBody className="col-span-5">
            <H4>Revolutionary Technology In Downstream Processing</H4>
            <P>
              The Thomson {logo.siteMetadata.brand.og} System of products is
              expanding into downstream processing with a revolutionary new
              technology that allows high speed clarification of cellular
              material. Thomson developed the {logo.siteMetadata.brand.rc} Cap
              to address the needs of scientists to quickly and efficiently
              clarify cell culture media directly from the Thomson 5L{" "}
              {logo.siteMetadata.brand.og} shaker flask without the need for
              centrifugation.
            </P>
          </CardBody>
          <CardImage
            alt={action.name}
            className="col-span-3"
            cover
            gatsbyImageData={action.childImageSharp.gatsbyImageData}
          />
        </Section>
      </Block>

      <Block theme="whitefull">
        <Header center tight>
          <H2>Features & Benefits</H2>
        </Header>
        <Section onecard normal>
          <CardBody className="col-span-4">
            <CardSub title="Key Features" />
            <Ul>
              <CardLi check>
                Depth filtration with a 0.2&mu;m final pore size eliminates
                multiple filtration steps and in most cases centrifugation
              </CardLi>
              <CardLi check>
                Significant time savings versus traditional spin down technique
              </CardLi>
              <CardLi check>
                Cell culture clarification of low or high density cultures of
                CHO stable, CHO transient, HEK293, hybridoma, and other
                mammalian cell lines
              </CardLi>
              <CardLi check>
                Eliminates transfer steps: The {logo.siteMetadata.brand.rc} Cap
                screws directly onto the {logo.siteMetadata.brand.og} Flask
              </CardLi>
              <CardLi check>
                Secondary cap attaches to a new {logo.siteMetadata.brand.og}{" "}
                Flask or to a storage container with a Luer lock
              </CardLi>
            </Ul>
          </CardBody>
          <CardBody className="col-span-4">
            <CardSub title="Key Benefits" />
            <Ul>
              <CardLi circle>
                Save time, clarify 3L of cell culture in less than 30 minutes -
                with no centrifugation required!
              </CardLi>
              <CardLi circle>Reduce consumables used by up to 90%</CardLi>
              <CardLi circle>
                Walk away convenience and safety - minimize endotoxin exposure
              </CardLi>
            </Ul>
          </CardBody>
        </Section>
      </Block>

      {/* SeriesCTA */}
      <SeriesCTA
        more
        title={`Visit More of the ${logo.siteMetadata.brand.og} System`}
      >
        <ProdCTAlink to="/og" color="purple" svg="OG">
          {logo.siteMetadata.brand.og} Flasks
        </ProdCTAlink>
        <ProdCTAlink to="/og/special" color="purple" svg="OG">
          {logo.siteMetadata.brand.og} Special Flasks
        </ProdCTAlink>
        <ProdCTAlink to="/og/tc" color="purple" svg="TC">
          Transfer Caps
        </ProdCTAlink>
        <ProdCTAlink to="/og/accessoriesog" color="purple" svg="OG">
          Accessories
        </ProdCTAlink>
      </SeriesCTA>

      <Block theme="whitefull">
        <Header center normal>
          <H2>See {logo.siteMetadata.brand.rc} Cap in Action!</H2>
        </Header>
        <Section onecard normal>
          <CardBody className="col-span-4">
            <P>
              Simply switch out the vented {logo.siteMetadata.brand.og} Cap for
              the {logo.siteMetadata.brand.rc}. The only equipment required is a
              peristaltic pump. In initial testing the units can filter up to
              2.5L-3L of high density culture, 6x10^6 -20x10^6 @ &gt;70%
              viability, in approximately 18 minutes. This technique will
              transform the time consuming and laborious task of harvesting
              cells to a rapid walk away procedure.
            </P>
          </CardBody>

          <CardBody className="col-span-4">
            <div className="rcplayerStyles">
              <iframe
                className="absolute left-0 top-0 h-full w-full"
                src="https://player.vimeo.com/video/525706654"
                width="640"
                height="360"
                frameBorder="0"
                allow="autoplay; fullscreen"
                title="Watch Rapid Clear in Action"
              ></iframe>
            </div>
          </CardBody>
        </Section>
      </Block>

      <Block theme="lightfull">
        <Header center snug>
          <H2>Win Your Day Back</H2>
          <H5> with Faster Filtration!</H5>
        </Header>
        <Section twocard tight>
          <Card
            alt={workday.name}
            imageHeight
            gatsbyImageData={workday.childImageSharp.gatsbyImageData}
          >
            <CardWrap snug>
              <H4>Time in Minutes</H4>
              <P>Clarify your cultures in under 30 minutes</P>
            </CardWrap>
          </Card>
          <Card
            alt={filtration.name}
            imageHeight
            gatsbyImageData={filtration.childImageSharp.gatsbyImageData}
          >
            <CardWrap snug>
              <H4>CHO & HEK293 Filtration</H4>
              <P>Quick cell harvest for mammalian cells</P>
            </CardWrap>
          </Card>
        </Section>
      </Block>

      <Block theme="lightfull">
        <Header center snug>
          <H2>Less Components Needed</H2>
          <H5>= Less Waste</H5>
        </Header>
        <Section twocard normal>
          <Card
            alt={oldM.name}
            imageHeight
            gatsbyImageData={oldM.childImageSharp.gatsbyImageData}
          >
            <CardWrap snug>
              <H4>Current Method</H4>
              <Ul>
                <Li>4 x filter funnels</Li>
                <Li>6 x containers</Li>
              </Ul>
              <H5>
                10 items
                <br /> + centerfugation needed
              </H5>
            </CardWrap>
          </Card>
          <Card
            alt={newM.name}
            imageHeight
            gatsbyImageData={newM.childImageSharp.gatsbyImageData}
          >
            <CardWrap snug>
              <H4>New Method</H4>
              <Ul>
                <Li>1 x {logo.siteMetadata.brand.og} Flask</Li>
                <Li>1 x {logo.siteMetadata.brand.rc} Cap</Li>
              </Ul>
              <H5>Only 2 items needed</H5>
            </CardWrap>
          </Card>
        </Section>
      </Block>

      <Block theme="darkfull">
        <Header center normal>
          <H2 className="text-white">A Comparison</H2>
          <H5 className="text-slate-300">
            {logo.siteMetadata.brand.rc} Cap 3000 to GE&reg; Capsule Filters
          </H5>
        </Header>
        <Section onecard snug>
          <CardBody className="col-span-3">
            <P>
              HPLC was utilized to quantitate intact protein for each of the
              purified solutions. The {logo.siteMetadata.brand.rc} System
              yielded a slightly higher quantity of intact IgG, than the GE
              capsule columns.
            </P>
          </CardBody>
          <CardImage
            alt={hplcgraph.name}
            className="col-span-5 bg-white"
            caption={`HPLC comparison of the purified IgG. The ${logo.siteMetadata.brand.rc} Cap 3000 is the red line and GE the blue line`}
            gatsbyImageData={hplcgraph.childImageSharp.gatsbyImageData}
          />
          <CardBody className="col-span-5">
            <P>
              Both GE and {logo.siteMetadata.brand.rc} clarified IgG were run on
              an SDS Page gel for comparison: Lanes 1 & 4 are molecular weight
              standard ladder; Lanes 2&3 are non-reduced; lanes 5&6 are reduced.
            </P>
            <Ul>
              <Li>1 ug of post proA protein per lane</Li>
              <Li>Both Reduced and Non-reduced samples</Li>
              <Li>4-12% Bis Tris Gel from Life Tech</Li>
              <Li>Run in MES buffer @ 200 V for 30 minutes</Li>
              <Li>Stained with Safe Stain</Li>
              <Li>Ladder is Precision Plus from Biorad</Li>
            </Ul>
            <P>
              <span className="font-bold text-orange-500">A</span>= GE filtered
              material
              <br />
              <span className="font-bold text-blue-600">B</span>={" "}
              {logo.siteMetadata.brand.rc}
              filtered material
            </P>
          </CardBody>
          <CardImage
            alt={gel.name}
            className="col-span-3 bg-white"
            gatsbyImageData={gel.childImageSharp.gatsbyImageData}
          />
        </Section>
      </Block>

      <Block theme="lightfull">
        <Header center normal>
          <H2>{logo.siteMetadata.brand.rc} Cap 3000 Protocol</H2>
        </Header>
        <Section onecard tight>
          <CardBody className="col-span-3">
            <H3>
              Reduce Operating Costs by Increasing Efficiency & Minimizing the
              Number of Consumables Used
            </H3>
            <P>
              When producing biologics, cell yield and viability, and effective
              clarification are critical. Thomson's patented{" "}
              {logo.siteMetadata.brand.og} Flask design facilitates good mixing
              and high gas exchange rates to produce high density yields of
              viable cells. Thomson has used its expertise in filtration to
              develop the {logo.siteMetadata.brand.rc} Cap 3000 to speed up the
              clarification process. This innovation reduces operating costs by
              increasing efficiency and minimizing the number of consumables
              used.
            </P>
          </CardBody>
          <CardBody className="col-span-5">
            <H4>Quick & Easy to Use</H4>
            <P>
              Using the {logo.siteMetadata.brand.rc} Cap 3000 is quick and easy.
              Once the mammalian or insect cell culture is ready for
              clarification, simply remove the vent cap and replace it with the{" "}
              {logo.siteMetadata.brand.rc} Cap 3000. The chart provides the
              approximate time<sup>***</sup> to clarify cell cultures based on
              cell type, viability and volume to be filtered. The higher the
              viability the faster the filtration.
            </P>
            <CardList>
              <CardLi>
                Grow cells in a 2.8L or 5L {logo.siteMetadata.brand.og} Flask
              </CardLi>
              <CardLi>
                Transfer the flask to a hood to replace the Vent Cap with the{" "}
                {logo.siteMetadata.brand.rc} Cap
              </CardLi>
              <CardLi>
                Replace the Vent Cap on the receiving 1.6L, 2.8L, or 5L{" "}
                {logo.siteMetadata.brand.og} Flask with the receiving cap that
                comes with the {logo.siteMetadata.brand.rc} Cap
              </CardLi>
              <CardLi>
                Remove from hood and place tubing in the pump head, see pump
                recommendations<sup>***</sup>
              </CardLi>
              <CardLi>
                Tilt the flask slightly and run the pump at maximum speed until
                only a few hundred mL remain
              </CardLi>
              <CardLi>
                Add 400mL of PBS to the {logo.siteMetadata.brand.og} Flask to
                ensure all the culture has been filtered and transferred to the
                new flask
              </CardLi>
            </CardList>
          </CardBody>
          <CardImage
            alt={protocolgraph.name}
            className="col-span-8 bg-white"
            caption={`Duplicate 3L CHO Stable cell cultures were clarified using the ${logo.siteMetadata.brand.rc} 3000. This graph compares the volume clarified over 21 minutes.`}
            gatsbyImageData={protocolgraph.childImageSharp.gatsbyImageData}
          />
        </Section>
      </Block>

      <Block theme="lightfull">
        <Header center tight>
          <H5>Volume & time recommendations by cell type</H5>
        </Header>
        <Section general tight>
          <RcProtocolTable />
          <Disclaimer>
            * For low viability cultures, (&lt; 39%), centrifuge for 7 minutes
            prior to clarifying with the Rapid Clear&reg; Cap.
            <br />
            ** This chart was created from results generated in customer labs.
            <br />
            *** All data was generated using a Cole-Parmer pump (pump drive p/n
            EW-07554-90, pump head p/n EW-77200-62)
            <br />
            **** Cell cultures that received 2+ feeds will require spinning to
            minimize potential clogging
          </Disclaimer>
        </Section>
      </Block>

      {/* CTA================================================== */}
      <ContactCTA
        to="/"
        cta="Nationwide Technical Support"
        title="We're here to help!"
        button="Let's talk"
      >
        Thomson employs a nationwide team of expert technical support staff
        ready to tackle any questions you may have regarding our products.
      </ContactCTA>

      {/* Part numbers================================================== */}
      <Block theme="lightfull">
        <Header center snug>
          <H4 id="partNumSection">{logo.siteMetadata.brand.rc} Part Number</H4>
        </Header>
        <Section general tight>
          <ProductList
            productQuery={rcpn}
            borderColor="ring-blue-500"
          ></ProductList>
        </Section>
      </Block>

      <Block theme="lightfull">
        <Header center tight>
          <H4>{logo.siteMetadata.brand.og} Flask Part Numbers</H4>
        </Header>
        <Section general normal>
          <ProductList
            productQuery={flaskpn}
            borderColor="ring-blue-500"
          ></ProductList>
        </Section>
      </Block>
    </Layout>
  )
}

export const query = graphql`
  {
    hero: file(relativePath: { eq: "page/hero/hero-rc.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
      }
    }
    action: file(relativePath: { eq: "page/rc-action.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    workday: file(relativePath: { eq: "page/rc-workday-graph.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    filtration: file(relativePath: { eq: "page/rc-filtration.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    oldM: file(relativePath: { eq: "page/rc-old-method.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    newM: file(relativePath: { eq: "page/rc-new-method.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    hplcgraph: file(relativePath: { eq: "page/rc-ge-hplc-graph.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    gel: file(relativePath: { eq: "page/rc-gel.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    protocolgraph: file(relativePath: { eq: "page/rc-protocol-graph.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
      }
    }
    logo: site {
      siteMetadata {
        brand {
          og
          rc
        }
      }
    }
    flaskpn: allProductsJson(
      filter: {
        series: { eq: "flask" }
        line: { eq: "OG" }
        image: { glob: "(931114.jpg|931116.jpg)" }
        partNumber: { elemMatch: {} }
      }
    ) {
      edges {
        node {
          id
          title
          desc_main
          slug
          desc_sub
          image
          quantity
          sterile
          partNumber {
            num
            qty
          }
          specs {
            workingVolume {
              key
              value
            }
            shakeSpeed {
              key
              value
            }
            topStyle {
              key
              value
            }
            cap {
              key
              value
            }
            baffles {
              key
              value
            }
            sterility {
              key
              value
            }
          }
        }
      }
    }
    rcpn: allProductsJson(
      filter: {
        series: { eq: "RC" }
        line: { eq: "OG" }
        partNumber: { elemMatch: {} }
      }
    ) {
      edges {
        node {
          id
          title
          desc_main
          slug
          desc_sub
          image
          quantity
          sterile
          partNumber {
            num
            qty
          }
          specs {
            flaskCompatibility {
              key
              value
            }
            tubing {
              key
              value
            }
            tubingConnection {
              key
              value
            }
            tubingDiameter {
              key
              value
            }
            material {
              key
              value
            }
            sterility {
              key
              value
            }
            notes {
              key
              value
            }
          }
        }
      }
    }
  }
`

export default Rc

export const Head = ({ data }) => (
  <Seo
    title={`Thomson ${data.logo.siteMetadata.brand.rc} cap`}
    description="A revolutionary new technology that allows high speed clarification of
          cellular material"
  />
)
